import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Battery, Shield, ArrowLeft, LogOut, Users, Mail, Package,
  Phone, MessageCircle, Calendar, Lock, Eye, EyeOff, Plus,
  Trash2, RefreshCw, CheckCircle, AlertCircle, Search, Settings,
  Edit3, X
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_energy-havolt/artifacts/x68m0xml_Final%20LOGO.png";

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("adminUsername", response.data.username);
      onLogin(response.data.token);
      toast.success("Login successful!");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Havolt" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold uppercase">Admin Login</h1>
          <p className="text-zinc-500 text-sm mt-2">Enter your credentials to access the dashboard</p>
        </div>

        <form onSubmit={handleLogin} className="bg-havolt-paper/50 border border-zinc-800 p-8 space-y-6">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-500 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 block">
              Username
            </Label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              className="bg-havolt-paper border-zinc-700 h-12"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 block">
              Password
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-havolt-paper border-zinc-700 h-12 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#18C964] text-black font-bold h-12 hover:bg-[#12A150]"
          >
            {isLoading ? "Logging in..." : "Login"}
          </Button>
        </form>

        <div className="text-center mt-6">
          <Link to="/" className="text-zinc-500 hover:text-[#18C964] text-sm flex items-center justify-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Website
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

// Dashboard Component
const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, inquiriesRes, batteriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader),
        axios.get(`${API}/admin/inquiries`, authHeader),
        axios.get(`${API}/warranty/batteries`, authHeader),
      ]);
      setStats(statsRes.data);
      setInquiries(inquiriesRes.data);
      setBatteries(batteriesRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      }
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, authHeader);
      setInquiries(inquiries.filter(i => i.id !== id));
      toast.success("Inquiry deleted");
    } catch (err) {
      toast.error("Failed to delete inquiry");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="bg-black/90 border-b border-zinc-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Havolt" className="h-10" />
              <span className="text-sm font-mono text-zinc-500">ADMIN PANEL</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-400">
                {localStorage.getItem("adminUsername")}
              </span>
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 hover:border-red-500 hover:text-red-500"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: "dashboard", label: "Dashboard", icon: Settings },
            { id: "products", label: "Products", icon: Package },
            { id: "batteries", label: "Batteries", icon: Battery },
            { id: "register", label: "Register Battery", icon: Plus },
            { id: "inquiries", label: "Inquiries", icon: Mail },
            { id: "settings", label: "Settings", icon: Lock },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 border transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-[#18C964]/10 border-[#18C964] text-[#18C964]"
                  : "bg-havolt-paper/50 border-zinc-800 text-zinc-400 hover:border-zinc-700"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 text-[#18C964] animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === "dashboard" && (
              <DashboardTab stats={stats} onRefresh={fetchData} />
            )}
            {activeTab === "products" && (
              <ProductsTab token={token} />
            )}
            {activeTab === "batteries" && (
              <BatteriesTab batteries={batteries} onRefresh={fetchData} token={token} />
            )}
            {activeTab === "register" && (
              <RegisterBatteryTab token={token} onSuccess={() => { fetchData(); setActiveTab("batteries"); }} />
            )}
            {activeTab === "inquiries" && (
              <InquiriesTab inquiries={inquiries} onDelete={deleteInquiry} />
            )}
            {activeTab === "settings" && (
              <SettingsTab token={token} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Dashboard Tab
const DashboardTab = ({ stats, onRefresh }) => {
  if (!stats) return null;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-heading font-bold uppercase">Dashboard Overview</h2>
        <Button onClick={onRefresh} variant="outline" size="sm" className="border-zinc-700">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-havolt-paper/50 border border-zinc-800 p-6">
          <Battery className="h-8 w-8 text-[#18C964] mb-4" />
          <div className="text-4xl font-heading font-bold text-white">{stats.total_batteries}</div>
          <div className="text-sm text-zinc-500">Registered Batteries</div>
        </div>
        <div className="bg-havolt-paper/50 border border-zinc-800 p-6">
          <Mail className="h-8 w-8 text-[#18C964] mb-4" />
          <div className="text-4xl font-heading font-bold text-white">{stats.total_inquiries}</div>
          <div className="text-sm text-zinc-500">Total Inquiries</div>
        </div>
        <div className="bg-havolt-paper/50 border border-zinc-800 p-6">
          <Users className="h-8 w-8 text-[#18C964] mb-4" />
          <div className="text-4xl font-heading font-bold text-white">{stats.total_subscribers}</div>
          <div className="text-sm text-zinc-500">Newsletter Subscribers</div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Inquiries */}
        <div className="bg-havolt-paper/50 border border-zinc-800 p-6">
          <h3 className="text-sm font-mono uppercase tracking-widest text-zinc-500 mb-4">Recent Inquiries</h3>
          {stats.recent_inquiries?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_inquiries.map((inquiry, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                  <div>
                    <div className="font-medium text-white">{inquiry.name}</div>
                    <div className="text-xs text-zinc-500">{inquiry.inquiry_type}</div>
                  </div>
                  <div className="text-xs text-zinc-500">{inquiry.phone}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No recent inquiries</p>
          )}
        </div>

        {/* Recent Batteries */}
        <div className="bg-havolt-paper/50 border border-zinc-800 p-6">
          <h3 className="text-sm font-mono uppercase tracking-widest text-zinc-500 mb-4">Recent Batteries</h3>
          {stats.recent_batteries?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_batteries.map((battery, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-zinc-800/30 border border-zinc-700">
                  <div>
                    <div className="font-mono text-[#18C964]">{battery.serial_number}</div>
                    <div className="text-xs text-zinc-500">{battery.customer_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">{battery.voltage}V / {battery.capacity}Ah</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No registered batteries</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Products Tab
const ProductsTab = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const categories = ["cells", "packs", "inverters", "bms", "grading"];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (err) {
      toast.error("Failed to fetch products");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(products.filter(p => p.id !== productId));
      toast.success("Product deleted");
    } catch (err) {
      toast.error("Failed to delete product");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 text-[#18C964] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-heading font-bold uppercase">Product Management</h2>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-[#18C964] text-black font-bold hover:bg-[#12A150]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-havolt-paper border-zinc-700 pl-10"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-48 bg-havolt-paper border-zinc-700">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-havolt-paper border-zinc-700">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      {filteredProducts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Product</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Category</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Price</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Stock</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {product.image_url && (
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                      )}
                      <div>
                        <div className="text-white font-medium">{product.name}</div>
                        <div className="text-xs text-zinc-500 line-clamp-1">{product.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-1 bg-zinc-800 text-zinc-300 text-xs rounded">
                      {product.category}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-[#18C964]">{formatPrice(product.price)}</td>
                  <td className="p-3">
                    {product.in_stock ? (
                      <span className="text-green-500 text-sm">In Stock</span>
                    ) : (
                      <span className="text-red-500 text-sm">Out of Stock</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setEditingProduct(product)}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:border-[#18C964] hover:text-[#18C964]"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => deleteProduct(product.id)}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:border-red-500 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-havolt-paper/50 border border-zinc-800">
          <Package className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">No products found</p>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {(showAddModal || editingProduct) && (
        <ProductModal
          product={editingProduct}
          token={token}
          onClose={() => {
            setShowAddModal(false);
            setEditingProduct(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setEditingProduct(null);
            fetchProducts();
          }}
        />
      )}
    </div>
  );
};

// Product Modal (Add/Edit)
const ProductModal = ({ product, token, onClose, onSuccess }) => {
  const isEditing = !!product;
  const [formData, setFormData] = useState({
    category: product?.category || "cells",
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price?.toString() || "",
    price_unit: product?.price_unit || "INR",
    image_url: product?.image_url || "",
    in_stock: product?.in_stock ?? true,
    is_featured: product?.is_featured ?? false,
    specs: product?.specs || [],
    features: product?.features || [],
    applications: product?.applications || [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [newSpec, setNewSpec] = useState({ label: "", value: "" });
  const [newFeature, setNewFeature] = useState("");
  const [newApplication, setNewApplication] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
      };

      if (isEditing) {
        await axios.put(`${API}/products/${product.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product updated successfully!");
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product created successfully!");
      }
      onSuccess();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = isEditing ? "Failed to update product" : "Failed to create product";
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const addSpec = () => {
    if (newSpec.label && newSpec.value) {
      setFormData({
        ...formData,
        specs: [...formData.specs, { ...newSpec }]
      });
      setNewSpec({ label: "", value: "" });
    }
  };

  const removeSpec = (index) => {
    setFormData({
      ...formData,
      specs: formData.specs.filter((_, i) => i !== index)
    });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const addApplication = () => {
    if (newApplication.trim()) {
      setFormData({
        ...formData,
        applications: [...formData.applications, newApplication.trim()]
      });
      setNewApplication("");
    }
  };

  const removeApplication = (index) => {
    setFormData({
      ...formData,
      applications: formData.applications.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-havolt-paper border border-zinc-800 w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4"
      >
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 sticky top-0 bg-havolt-paper z-10">
          <h3 className="text-lg font-heading font-bold uppercase">
            {isEditing ? "Edit Product" : "Add New Product"}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger className="bg-havolt-paper border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-havolt-paper border-zinc-700">
                  <SelectItem value="cells">LiFePO4 Cells</SelectItem>
                  <SelectItem value="packs">Battery Packs</SelectItem>
                  <SelectItem value="inverters">Inverters</SelectItem>
                  <SelectItem value="bms">BMS Systems</SelectItem>
                  <SelectItem value="grading">Cell Grading</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Price (INR) *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Product Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-havolt-paper border-zinc-700"
              placeholder="e.g., EVE LF280K 280Ah Cell"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-havolt-paper border-zinc-700 min-h-[80px]"
              placeholder="Product description (min 10 characters)"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Image URL</Label>
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              className="bg-havolt-paper border-zinc-700"
              placeholder="https://..."
            />
          </div>

          {/* Specs */}
          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Specifications</Label>
            <div className="space-y-2 mb-2">
              {formData.specs.map((spec, i) => (
                <div key={i} className="flex items-center gap-2 bg-zinc-800/50 p-2 rounded">
                  <span className="text-zinc-400">{spec.label}:</span>
                  <span className="text-white">{spec.value}</span>
                  <button type="button" onClick={() => removeSpec(i)} className="ml-auto text-red-500 hover:text-red-400">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newSpec.label}
                onChange={(e) => setNewSpec({ ...newSpec, label: e.target.value })}
                className="bg-havolt-paper border-zinc-700 flex-1"
                placeholder="Label (e.g., Voltage)"
              />
              <Input
                value={newSpec.value}
                onChange={(e) => setNewSpec({ ...newSpec, value: e.target.value })}
                className="bg-havolt-paper border-zinc-700 flex-1"
                placeholder="Value (e.g., 3.2V)"
              />
              <Button type="button" onClick={addSpec} variant="outline" className="border-zinc-700">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Features */}
          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Features</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.features.map((feature, i) => (
                <span key={i} className="px-3 py-1 bg-zinc-800 text-zinc-300 text-sm rounded-full flex items-center gap-2">
                  {feature}
                  <button type="button" onClick={() => removeFeature(i)} className="text-red-500 hover:text-red-400">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                className="bg-havolt-paper border-zinc-700"
                placeholder="Add feature"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
              />
              <Button type="button" onClick={addFeature} variant="outline" className="border-zinc-700">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Applications */}
          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Applications</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.applications.map((app, i) => (
                <span key={i} className="px-3 py-1 bg-zinc-800 text-zinc-300 text-sm rounded-full flex items-center gap-2">
                  {app}
                  <button type="button" onClick={() => removeApplication(i)} className="text-red-500 hover:text-red-400">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newApplication}
                onChange={(e) => setNewApplication(e.target.value)}
                className="bg-havolt-paper border-zinc-700"
                placeholder="Add application"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addApplication())}
              />
              <Button type="button" onClick={addApplication} variant="outline" className="border-zinc-700">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.in_stock}
                onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                className="w-4 h-4 rounded border-zinc-700 bg-havolt-paper text-[#18C964] focus:ring-[#18C964]"
              />
              <span className="text-zinc-300">In Stock</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="w-4 h-4 rounded border-zinc-700 bg-havolt-paper text-[#18C964] focus:ring-[#18C964]"
              />
              <span className="text-zinc-300">Featured Product</span>
            </label>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-[#18C964] text-black font-bold hover:bg-[#12A150]"
            >
              {isLoading ? "Saving..." : (isEditing ? "Update Product" : "Create Product")}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};


// Edit Battery Modal
const EditBatteryModal = ({ battery, token, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    customer_name: battery.customer_name || "",
    customer_phone: battery.customer_phone || "",
    customer_email: battery.customer_email || "",
    voltage: battery.voltage?.toString() || "",
    capacity: battery.capacity?.toString() || "",
    cells_series: battery.cells_series?.toString() || "",
    cells_parallel: battery.cells_parallel?.toString() || "",
    bms_config: battery.bms_config || "",
    application: battery.application || "",
    purchase_date: battery.purchase_date || "",
    warranty_years: battery.warranty_years?.toString() || "3",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Build payload with only changed fields
      const payload = {};
      
      if (formData.customer_name !== battery.customer_name) payload.customer_name = formData.customer_name;
      if (formData.customer_phone !== battery.customer_phone) payload.customer_phone = formData.customer_phone;
      if (formData.customer_email && formData.customer_email.trim() && formData.customer_email !== battery.customer_email) {
        payload.customer_email = formData.customer_email.trim();
      }
      if (parseFloat(formData.voltage) !== battery.voltage) payload.voltage = parseFloat(formData.voltage);
      if (parseInt(formData.capacity) !== battery.capacity) payload.capacity = parseInt(formData.capacity);
      if (parseInt(formData.cells_series) !== battery.cells_series) payload.cells_series = parseInt(formData.cells_series);
      if (parseInt(formData.cells_parallel) !== battery.cells_parallel) payload.cells_parallel = parseInt(formData.cells_parallel);
      if (formData.bms_config !== battery.bms_config) payload.bms_config = formData.bms_config;
      if (formData.application && formData.application.trim() !== (battery.application || "")) {
        payload.application = formData.application.trim();
      }
      if (formData.purchase_date !== battery.purchase_date) payload.purchase_date = formData.purchase_date;
      if (parseInt(formData.warranty_years) !== battery.warranty_years) payload.warranty_years = parseInt(formData.warranty_years);

      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        onClose();
        return;
      }

      await axios.put(`${API}/warranty/batteries/${battery.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Battery updated successfully!");
      onSuccess();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = "Failed to update battery";
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-havolt-paper border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4"
      >
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-lg font-heading font-bold uppercase">Edit Battery</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Serial Number (Read-only) */}
          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Serial Number</Label>
            <Input
              value={battery.serial_number}
              disabled
              className="bg-zinc-800 border-zinc-700 font-mono text-[#18C964] opacity-70"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Customer Name *</Label>
              <Input
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Customer Phone *</Label>
              <Input
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div className="col-span-2">
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Customer Email</Label>
              <Input
                name="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={handleChange}
                placeholder="email@example.com"
                className="bg-havolt-paper border-zinc-700"
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Voltage (V) *</Label>
              <Input
                name="voltage"
                type="number"
                step="0.1"
                value={formData.voltage}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Capacity (Ah) *</Label>
              <Input
                name="capacity"
                type="number"
                value={formData.capacity}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Cells in Series *</Label>
              <Input
                name="cells_series"
                type="number"
                value={formData.cells_series}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Cells in Parallel *</Label>
              <Input
                name="cells_parallel"
                type="number"
                value={formData.cells_parallel}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">BMS Config *</Label>
              <Input
                name="bms_config"
                value={formData.bms_config}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Application</Label>
              <Input
                name="application"
                value={formData.application}
                onChange={handleChange}
                placeholder="Solar Inverter"
                className="bg-havolt-paper border-zinc-700"
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Purchase Date *</Label>
              <Input
                name="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={handleChange}
                className="bg-havolt-paper border-zinc-700"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Warranty Years</Label>
              <Select
                value={formData.warranty_years}
                onValueChange={(v) => setFormData({ ...formData, warranty_years: v })}
              >
                <SelectTrigger className="bg-havolt-paper border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-havolt-paper border-zinc-700">
                  <SelectItem value="1">1 Year</SelectItem>
                  <SelectItem value="2">2 Years</SelectItem>
                  <SelectItem value="3">3 Years</SelectItem>
                  <SelectItem value="5">5 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-[#18C964] text-black font-bold hover:bg-[#12A150]"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};


// Batteries Tab
const BatteriesTab = ({ batteries, onRefresh, token }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editingBattery, setEditingBattery] = useState(null);
  const [isDeleting, setIsDeleting] = useState(null);

  // Enhanced search: serial number, customer name, OR phone number
  const filteredBatteries = batteries.filter(b =>
    b.serial_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.customer_phone.includes(searchTerm)
  );

  const handleDelete = async (batteryId) => {
    if (!window.confirm("Are you sure you want to delete this battery record? This action cannot be undone.")) return;
    
    setIsDeleting(batteryId);
    try {
      await axios.delete(`${API}/warranty/batteries/${batteryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Battery deleted successfully");
      onRefresh();
    } catch (err) {
      toast.error("Failed to delete battery");
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-2xl font-heading font-bold uppercase">Registered Batteries</h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Search by serial, name, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-havolt-paper border-zinc-700 pl-10 w-72"
            />
          </div>
          <Button onClick={onRefresh} variant="outline" size="sm" className="border-zinc-700">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {filteredBatteries.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Serial Number</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Customer</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Specs</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Purchase Date</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Warranty Expiry</th>
                <th className="text-left p-3 text-xs font-mono uppercase text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBatteries.map((battery) => (
                <tr key={battery.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-3 font-mono text-[#18C964]">{battery.serial_number}</td>
                  <td className="p-3">
                    <div className="text-white">{battery.customer_name}</div>
                    <div className="text-xs text-zinc-500">{battery.customer_phone}</div>
                  </td>
                  <td className="p-3 text-white">{battery.voltage}V / {battery.capacity}Ah</td>
                  <td className="p-3 text-zinc-400">{battery.purchase_date}</td>
                  <td className="p-3 text-zinc-400">{battery.warranty_expiry}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setEditingBattery(battery)}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:border-[#18C964] hover:text-[#18C964]"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(battery.id)}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:border-red-500 hover:text-red-500"
                        disabled={isDeleting === battery.id}
                      >
                        {isDeleting === battery.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-havolt-paper/50 border border-zinc-800">
          <Battery className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">No batteries found</p>
        </div>
      )}

      {/* Edit Battery Modal */}
      {editingBattery && (
        <EditBatteryModal
          battery={editingBattery}
          token={token}
          onClose={() => setEditingBattery(null)}
          onSuccess={() => {
            setEditingBattery(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

// Register Battery Tab
const RegisterBatteryTab = ({ token, onSuccess }) => {
  const [formData, setFormData] = useState({
    serial_number: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    voltage: "",
    capacity: "",
    cells_series: "",
    cells_parallel: "",
    bms_config: "",
    application: "",
    purchase_date: new Date().toISOString().split("T")[0],
    warranty_years: "3",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Clean up empty optional fields before sending
      const payload = {
        serial_number: formData.serial_number,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        voltage: parseFloat(formData.voltage),
        capacity: parseInt(formData.capacity),
        cells_series: parseInt(formData.cells_series),
        cells_parallel: parseInt(formData.cells_parallel),
        bms_config: formData.bms_config,
        purchase_date: formData.purchase_date,
        warranty_years: parseInt(formData.warranty_years),
      };
      
      // Only add optional fields if they have values
      if (formData.customer_email && formData.customer_email.trim()) {
        payload.customer_email = formData.customer_email.trim();
      }
      if (formData.application && formData.application.trim()) {
        payload.application = formData.application.trim();
      }
      
      await axios.post(`${API}/warranty/register`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Battery registered successfully!");
      onSuccess();
    } catch (err) {
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const errorDetail = err.response?.data?.detail;
      let errorMessage = "Failed to register battery";
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-heading font-bold uppercase mb-6">Register New Battery</h2>

      <form onSubmit={handleSubmit} className="bg-havolt-paper/50 border border-zinc-800 p-8 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Serial Number *</Label>
            <Input
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              placeholder="HVT-48V-100AH-001"
              className="bg-havolt-paper border-zinc-700 font-mono uppercase"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Customer Name *</Label>
            <Input
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              placeholder="Customer name"
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Customer Phone *</Label>
            <Input
              name="customer_phone"
              value={formData.customer_phone}
              onChange={handleChange}
              placeholder="9876543210"
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Customer Email</Label>
            <Input
              name="customer_email"
              type="email"
              value={formData.customer_email}
              onChange={handleChange}
              placeholder="email@example.com"
              className="bg-havolt-paper border-zinc-700"
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Voltage (V) *</Label>
            <Input
              name="voltage"
              type="number"
              step="0.1"
              value={formData.voltage}
              onChange={handleChange}
              placeholder="48"
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Capacity (Ah) *</Label>
            <Input
              name="capacity"
              type="number"
              value={formData.capacity}
              onChange={handleChange}
              placeholder="100"
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Cells in Series *</Label>
            <Input
              name="cells_series"
              type="number"
              value={formData.cells_series}
              onChange={handleChange}
              placeholder="15"
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Cells in Parallel *</Label>
            <Input
              name="cells_parallel"
              type="number"
              value={formData.cells_parallel}
              onChange={handleChange}
              placeholder="7"
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">BMS Config *</Label>
            <Input
              name="bms_config"
              value={formData.bms_config}
              onChange={handleChange}
              placeholder="15S7P"
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Application</Label>
            <Input
              name="application"
              value={formData.application}
              onChange={handleChange}
              placeholder="Solar Inverter"
              className="bg-havolt-paper border-zinc-700"
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Purchase Date *</Label>
            <Input
              name="purchase_date"
              type="date"
              value={formData.purchase_date}
              onChange={handleChange}
              className="bg-havolt-paper border-zinc-700"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Warranty Years</Label>
            <Select
              value={formData.warranty_years}
              onValueChange={(v) => setFormData({ ...formData, warranty_years: v })}
            >
              <SelectTrigger className="bg-havolt-paper border-zinc-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-havolt-paper border-zinc-700">
                <SelectItem value="1">1 Year</SelectItem>
                <SelectItem value="2">2 Years</SelectItem>
                <SelectItem value="3">3 Years</SelectItem>
                <SelectItem value="5">5 Years</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#18C964] text-black font-bold h-12 hover:bg-[#12A150]"
        >
          {isLoading ? "Registering..." : "Register Battery"}
        </Button>
      </form>
    </div>
  );
};

// Inquiries Tab
const InquiriesTab = ({ inquiries, onDelete }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-heading font-bold uppercase">Customer Inquiries</h2>

      {inquiries.length > 0 ? (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <div key={inquiry.id} className="bg-havolt-paper/50 border border-zinc-800 p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">{inquiry.name}</h3>
                  <div className="flex items-center gap-4 text-sm text-zinc-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      {inquiry.phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {inquiry.email}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-[#18C964]/10 text-[#18C964] text-xs font-mono uppercase">
                    {inquiry.inquiry_type}
                  </span>
                  <Button
                    onClick={() => onDelete(inquiry.id)}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-red-500 hover:border-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {(inquiry.voltage || inquiry.capacity) && (
                <div className="flex gap-4 mb-3 text-sm">
                  {inquiry.voltage && <span className="text-zinc-400">Voltage: <span className="text-white">{inquiry.voltage}</span></span>}
                  {inquiry.capacity && <span className="text-zinc-400">Capacity: <span className="text-white">{inquiry.capacity}</span></span>}
                  {inquiry.application && <span className="text-zinc-400">Application: <span className="text-white">{inquiry.application}</span></span>}
                </div>
              )}

              <p className="text-zinc-300 bg-zinc-800/50 p-3 border border-zinc-700">{inquiry.message}</p>

              <div className="flex gap-3 mt-4">
                <a
                  href={`https://wa.me/91${inquiry.phone}?text=Hi ${inquiry.name}, regarding your inquiry about Havolt batteries...`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="sm" className="bg-[#25D366] hover:bg-[#20BD5A] text-white">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <a href={`tel:+91${inquiry.phone}`}>
                  <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300">
                    <Phone className="h-4 w-4 mr-2" />
                    Call
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-havolt-paper/50 border border-zinc-800">
          <Mail className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">No inquiries yet</p>
        </div>
      )}
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ token }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const errorDetail = err.response?.data?.detail;
      let errorMessage = "Failed to change password";
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md">
      <h2 className="text-2xl font-heading font-bold uppercase mb-6">Change Password</h2>

      <form onSubmit={handleChangePassword} className="bg-havolt-paper/50 border border-zinc-800 p-8 space-y-6">
        <div>
          <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Current Password</Label>
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="bg-havolt-paper border-zinc-700"
            required
          />
        </div>

        <div>
          <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">New Password</Label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="bg-havolt-paper border-zinc-700"
            required
          />
        </div>

        <div>
          <Label className="text-xs font-mono uppercase text-zinc-500 mb-2 block">Confirm New Password</Label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="bg-havolt-paper border-zinc-700"
            required
          />
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#18C964] text-black font-bold h-12 hover:bg-[#12A150]"
        >
          {isLoading ? "Changing..." : "Change Password"}
        </Button>
      </form>
    </div>
  );
};

// Main Admin Component
const AdminPanel = () => {
  const [token, setToken] = useState(localStorage.getItem("adminToken"));
  const [isVerifying, setIsVerifying] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    if (!token) {
      setIsVerifying(false);
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsVerifying(false);
    } catch (err) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUsername");
      setToken(null);
      setIsVerifying(false);
    }
  };

  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUsername");
    setToken(null);
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-[#18C964] animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" toastOptions={{
        style: { background: '#18181B', color: '#FAFAFA', border: '1px solid #27272A' }
      }} />
      {token ? (
        <AdminDashboard token={token} onLogout={handleLogout} />
      ) : (
        <AdminLogin onLogin={handleLogin} />
      )}
    </>
  );
};

export default AdminPanel;
